define('../app/fukit/model/base.model',['require','exports','module','lodash','datakit/datakit','../collection/base.collection','evicommon/search.util','evicommon/pagination.util','evicommon/state/changes.helper','backbone.radio'],function(require, exports, module) {
  //prettier-ignore
  var lodash         = require('lodash'),
        DataKit        = require('datakit/datakit'),
        BaseCollection = require('../collection/base.collection'),
        SearchUtil     = require('evicommon/search.util'),
        PaginationUtil = require('evicommon/pagination.util'),
        ChangesHelper  = require('evicommon/state/changes.helper'),
        Radio          = require('backbone.radio');

  var BaseModel = DataKit.HalModel.extend(
    {
      selfLinkIdExtractionRegex: /([^/?]+)$/,

      constructor: function() {
        DataKit.HalModel.apply(this, arguments);
        this.enableCreateEvent();
        this.attachValidationHandling(this);
      },

      // The defaults and parse methods make reference to a parameter called boundParameters.  This array was introduced
      //  to handle the following issue.  If the server sends the data for a model and that data is nested in such a way
      //  that sometimes the response looks like {foo: {bar: 'baz'}} and other times like {foo: null}.

      //  Example: If 'foo.bar' is tied to an input, then the act of binding that value to the input will change the
      //    model to become {foo: {bar: null}}. If 'foo.bar' is in the boundParameters array, then the model will begin
      //    its life with {foo: {bar: null}} in its model and will keep that structure even if the api sends {foo: null}
      //    in its response.

      // Usage: boundParameters is an array of all parameters that have these criteria:
      //  - they are nested deeper than the base level of the model
      //  - they are bound to inputs
      // defaults and parse will handle the bound parameters correctly once they are included.
      // The defaultsObject argument allows extensions off of the base model to define their
      //  own defaults method that calls this one with some preconfigured values.
      defaults: function(defaultsObject) {
        var childDefaults = {};
        var childModel;
        // If there are any child models, use their defaults
        for (var childModelProp in this.childModels || {}) {
          childModel = this.childModels[childModelProp];
          if (typeof childModel === 'string') {
            childModel = Radio.request('models', childModel);
          }
          if (lodash.isFunction(childModel.prototype.defaults)) {
            childDefaults[childModelProp] = childModel.prototype.defaults();
          } else if (!lodash.isEmpty(childModel.prototype.defaults)) {
            childDefaults[childModelProp] = childModel.prototype.defaults;
          }
        }

        // If there are boundParameters, then set all of those values to null
        return lodash.reduce(
          this.boundParameters || [],
          function(acc, parameter) {
            if (lodash.has(acc, parameter)) {
              return acc;
            }
            return lodash.set(acc, parameter, null);
          },
          // The defaults that are passed in extend and supersede the child defaults
          lodash.extend(childDefaults, defaultsObject)
        );
      },

      initialize: function() {
        DataKit.HalModel.prototype.initialize.apply(this, arguments);

        // Another way a model can arrive from a request is part of a collection request. Here we handle V2 removing
        // endpoints by checking those models on "add" events, and inserting an ID into the model from its "self" href
        this.on(
          'add',
          function(model, collection, options) {
            if (!this._hasIdAttribute() && this.hasLink('self')) {
              this._setIdFromSelfLink(this.getLink('self').get('href'));
            }
          },
          this
        );
      },

      // The isNew function references the idAttributes parameter.  This parameter is used for the following issue.  If the
      //  server's response for a model doesn't include an id on the base level of its response object, then the default
      //  isNew behavior will think that the model hasn't been fetched.  If the api sends a response that looks like
      //  {foo: {id: <uuid>}, bar: {id: <uuid>}}, then the model should have idAttributes: ['foo.id', 'bar.id'].
      isNew: function() {
        var model = this;
        // The default behavior is to return !model.has('id')
        // If the model has idAttributes, then check that at least one is present
        return !lodash.some(model.idAttributes || ['id'], function(idAttribute) {
          return model.has(idAttribute);
        });
      },

      _setIdFromSelfLink: function(selfHref) {
        var idValue = this._getIdValueFromSelfLink(selfHref);

        this.set('id', idValue);
      },

      _getIdValueFromSelfLink: function(selfHref) {
        return this.selfLinkIdExtractionRegex.exec(selfHref)[0];
      },

      _hasIdAttribute: function() {
        return this.get(this.idAttribute) ? true : false;
      },

      _responseHasSelfLink: function(resp) {
        return resp && resp._links && resp._links.self ? true : false;
      },

      parse: function(resp, options) {
        if (lodash.has(resp, 'data')) {
          // ## LEGACY SUPPORT
          return resp.data;
        }

        // V2 api endpoints no longer include the ID of an entity as a returned attribute in our models, but much of our
        // framework's logic for evaluating "same" data objects centers around matching IDs.  In HAL, the "identity" of
        // a model is its "self" link, which includes the ID.  So we check to see if a model does not have an ID but has
        // a self link, we parse the ID from the self link and set it in the model for legacy support.
        if (!this._hasIdAttribute() && this._responseHasSelfLink(resp)) {
          resp.id = this._getIdValueFromSelfLink(resp._links.self.href);
        }

        if (this.boundParameters) {
          // If there are bound parameters or defaults, be sure to maintain their values unless
          //  the fetched model has replacements

          // get the defaults for the model
          var defaults = lodash.isFunction(this.defaults)
            ? // If this.defaults is a function, call it with appropriate context.
              this.defaults.bind(this)()
            : // Otherwise use the defaults object (or substitute an empty object as a fallback)
              this.defaults || {};
          // cleanedData is the defaults after they've been overwritten by the response
          var cleanedData = lodash.assign(defaults, resp);
          lodash.each(this.boundParameters, function(parameter) {
            var cleanedValue = lodash.get(cleanedData, parameter, null);
            // If a number is bound to an input field, make sure it is coerced to a string.
            //  Otherwise the changes helper will not accurately check for changes to the input.
            if (lodash.isNumber(cleanedValue)) {
              cleanedValue = cleanedValue + '';
            }
            lodash.set(cleanedData, parameter, cleanedValue);
          });
          return DataKit.HalModel.prototype.parse.apply(this, [cleanedData, options]);
        }

        return DataKit.HalModel.prototype.parse.apply(this, arguments);
      },

      enableCreateEvent: function() {
        if (this.isNew()) {
          this.once(
            'sync',
            function(model, options) {
              this.trigger('create', model, options);
            },
            this
          );
        }
        return this;
      },

      enableUnsavedChangesEvent: function() {
        if (!this._unsavedChanges) {
          this._unsavedChanges = new ChangesHelper();
          this._unsavedChanges.add(this);

          this.listenTo(this._unsavedChanges, 'change:' + ChangesHelper.UNSAVED_CHANGES, function() {
            this.trigger('unsavedChanges');
          });
        }
        return this;
      },

      resetUnsavedChanges: function() {
        if (this._unsavedChanges) {
          this._unsavedChanges.reset();
        }
        return this;
      },

      getUnsavedChangesModel: function() {
        return this._unsavedChanges;
      },

      hasUnsavedChanges: function() {
        return this._unsavedChanges && this._unsavedChanges.get(ChangesHelper.UNSAVED_CHANGES);
      },

      attachValidationHandling: function(model) {
        model
          .on('error', function(model, error) {
            if (!error.xhr) {
              return;
            }

            var _remapValidations = function(results) {
              return lodash.map(results || [], function(r) {
                return {
                  field: r.fieldId,
                  message: r.message,
                };
              });
            };

            var data = error.xhr.responseJSON || {},
              validations = this.parseValidations(data.errors || _remapValidations(data.validations)),
              isValid = validations.length == 0;

            this.trigger('validated', model, isValid, validations);
          })
          .on('sync', function() {
            this.trigger('validated', model, true, []);
          });

        return model;
      },

      parseValidations: function(validations) {
        return validations;
      },

      serializeChildModel: function(model, options) {
        return model.toJSON(options);
      },
    },
    {
      Collection: BaseCollection,
    }
  );

  module.exports = BaseModel;
});

